import { LowerCasePipe, NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { untracked } from '@angular/core/primitives/signals';
import { FormsModule } from '@angular/forms';
import { LogsService } from '@services/logs';
import { DropdownComponent } from '@shared/components/dropdown';
import { Dropdown } from '@shared/components/dropdown/model/dropdown';
import { FormItemThemeEnum } from '@shared/components/form-item/enum';
import { FormItem, FormItemEmit, ValueType } from '@shared/components/form-item/model';
import {
  checkDate,
  checkNumeric,
  checkText,
  updateDateMinMax,
  updateNumericMinMax,
} from '@shared/components/form-item/validators';
import { debounceTime, Subject, takeUntil } from 'rxjs';

interface SearchDropdown {
  list: Dropdown[];
  isOpen: boolean;
}

@Component({
  selector: 'app-form-item',
  templateUrl: './form-item.component.html',
  styleUrl: './form-item.component.scss',
  imports: [FormsModule, NgClass, LowerCasePipe, NgStyle, DropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormItemComponent implements OnDestroy {
  readonly specs = input.required<FormItem>();
  readonly debounceTime = input(500);
  readonly theme = input<keyof typeof FormItemThemeEnum>('OUTLINE');
  readonly resetTrigger = input<number>(0);
  readonly value = output<FormItemEmit>();
  readonly customStyle = input<{
    [klass: string]: string | number;
  }>({});
  readonly isMobile = input(true);
  val!: ValueType;
  tips!: string | null;
  isError = false;
  dropdown: SearchDropdown = {
    list: [],
    isOpen: false,
  };
  private readonly subjectInputValue: Subject<ValueType> = new Subject();
  private readonly destroy$ = new Subject<boolean>();
  private readonly previousResetTrigger = signal(-1);
  readonly #logsService = inject(LogsService);

  constructor() {
    effect(() => {
      if (this.specs()) {
        this.watchAndSendValue();
        this.initVar();
      }
    });
    effect(() => {
      const reset = this.resetTrigger();
      const specs = untracked(this.specs);
      const previousResetTrigger = untracked(this.previousResetTrigger);
      if (!!reset && reset !== previousResetTrigger && specs) {
        this.previousResetTrigger.set(reset);
        this.#logsService.debug(
          'FormItemComponent',
          `Reset trigger value changed to ${reset}. Form item specs: ${specs.key}`
        );
        this.resetValue();
      }
    });
  }

  public valueChange(value: ValueType): void {
    this.subjectInputValue.next(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onSearchSelectClick(): void {
    this.dropdown.isOpen = !this.dropdown.isOpen;
  }

  onSearchDropdownSelected(item: Dropdown): void {
    this.dropdown.isOpen = false;
    this.val = item.label;
    this.value.emit({ key: this.specs().key, value: item, isValid: true });
  }

  private watchAndSendValue(): void {
    this.subjectInputValue
      .pipe(takeUntil(this.destroy$), debounceTime(this.debounceTime()))
      .subscribe((value: ValueType) => {
        this.val = value;
        if (this.isValid(value)) {
          this.isError = false;
          this.value.emit({
            key: this.specs().key,
            value: this.val,
            isValid: true,
          });
        } else {
          this.isError = true;
          this.value.emit({ key: this.specs().key, value: null, isValid: false });
        }
      });
  }

  private initVar(): void {
    const specs = this.specs();
    if (specs.value) {
      if (specs.type === 'BOOLEAN') {
        this.val = specs.value ?? false;
      } else {
        this.val = specs.value;
      }
      this.subjectInputValue.next(this.val);
    }
    if (specs.type === 'LIST' && specs.placeholder) {
      this.val = specs.placeholder;
    }
    if (specs.type === 'SEARCH') {
      this.dropdown.list = (specs.list as Dropdown[]) ?? [];
    }
    this.tips = specs?.rules?.tips ?? null;
  }

  private isValid(value: ValueType): boolean {
    const specs = this.specs();
    if (specs.type === 'NUMERIC') {
      this.val = updateNumericMinMax(value as number, specs?.rules);
      return checkNumeric(this.val, specs?.rules);
    } else if (specs.type === 'DATE') {
      this.val = updateDateMinMax(value as string, specs?.rules);
      return checkDate(this.val);
    }
    return checkText(this.val, specs?.rules);
  }

  private resetValue(): void {
    const specs = this.specs();
    this.isError = false;
    this.dropdown.isOpen = false;

    switch (specs.type) {
      case 'BOOLEAN':
        this.val = false;
        break;
      case 'LIST':
        this.val = specs.placeholder ?? '';
        break;
      default:
        this.val = '';
        break;
    }

    this.value.emit({ key: specs.key, value: null, isValid: false });
  }
}
